import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Button, Dropdown, Space } from 'antd';
import { MlsNumber, Plan, PlanName } from '@bb/automaton';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';
import { Icon, Avatar } from 'components';
import RoleTag from 'components/RoleTag';
import { featureEnabled } from 'utils';
import SupraCoopTypeTag from '../../MLSStaffPages/Tags/CoopTypeTag';
import { UserStatusTag } from '../../../components';
import {
  DetailText,
  DetailTextPadded,
  FlexAlign,
  FlexColumn,
  UserSectionTop,
  PadRight,
  NameContainer,
} from '../styles';

const planMap: Record<Plan['name'], string> = {
  AGENT: 'Agent',
  BROKERAGE: 'Brokerage',
};

type UserHeaderProps = {
  user: any;
  coopType?: SupraCoopType;
  features: any;
  plan: PlanName;
  menuProps: any;
  handleEditClick: any;
  isAssumeDisabled?: boolean;
  handleAssumeClick: any;
  isChatDisabled: boolean;
  handleChatClick: any;
};

const UserHeader: React.FC<UserHeaderProps> = ({
  user,
  coopType = SupraCoopType.PRIMARY,
  features,
  plan,
  menuProps,
  handleEditClick,
  isAssumeDisabled,
  handleAssumeClick,
  isChatDisabled,
  handleChatClick,
}) => {
  const mlsNumbers = useMemo(
    () =>
      user?.mls_numbers?.map(
        ({
          board,
          number,
          mlsUniqueId,
        }: MlsNumber & { mlsUniqueId?: string }) => {
          // Display keyholderId (mapped mlsUniqueId) instead of mlsNumber for data connect users
          const memberId =
            featureEnabled('membershipKeyholderIdSupportFeature', 1) &&
            user.usingDataConnectAPI &&
            mlsUniqueId
              ? mlsUniqueId
              : number;
          return `${board}: ${memberId}`;
        },
      ),
    [user],
  );

  return (
    <>
      <UserSectionTop>
        <PadRight>
          <Avatar user={user} width={93} />
        </PadRight>
        <FlexColumn style={{ width: '100%' }}>
          <NameContainer>
            <FlexAlign>
              <UserName>
                {user.firstName} {user.lastName}
              </UserName>
              {(user.pronoun?.length ?? 0) > 0 && (
                <UserPronouns>({user.pronoun!.join(', ')})</UserPronouns>
              )}
            </FlexAlign>
            {(!featureEnabled('supraAutoCoopFeature') ||
              coopType === SupraCoopType.PRIMARY) &&
              featureEnabled('mlsStaffActionButtonsFeature') && (
                <Dropdown menu={menuProps}>
                  <Button data-cy="action-dropdown-btn">
                    <Space>
                      Actions
                      <Icon type="antdDownOutlined" width={12} height={12} />
                    </Space>
                  </Button>
                </Dropdown>
              )}
            {(!featureEnabled('supraAutoCoopFeature') ||
              coopType === SupraCoopType.PRIMARY) &&
              !featureEnabled('mlsStaffActionButtonsFeature') &&
              features.includes('MLS_STAFF_ADD_USERS') && (
                <Button
                  icon={
                    <Icon type="antdEditFilled" width={12} offset={[0, -2]} />
                  }
                  onClick={handleEditClick}
                >
                  Edit
                </Button>
              )}
          </NameContainer>

          {(!featureEnabled('supraAutoCoopFeature') ||
            coopType === SupraCoopType.PRIMARY) && (
            <DetailText>
              {!featureEnabled('supraAutoCoopFeature') && user.org.name}
              {user.team?.name && ` · ${user.team.name}`}
            </DetailText>
          )}

          <FlexAlignPadded>
            {featureEnabled('supraAutoCoopFeature') &&
              coopType !== SupraCoopType.PRIMARY && (
                <SupraCoopTypeTag coopType={coopType} />
              )}
            <RoleTag role={user.role} />
            <UserStatusTag
              inline
              disabled={user.disabled}
              isUnverified={user.isUnverified}
            />
          </FlexAlignPadded>

          {(!featureEnabled('supraAutoCoopFeature') ||
            coopType === SupraCoopType.PRIMARY) &&
            plan && (
              <DetailTextPadded>
                BrokerBay {planMap[plan]} Plan
              </DetailTextPadded>
            )}

          {mlsNumbers && (
            <DetailTextPadded>
              {featureEnabled('membershipKeyholderIdSupportFeature', 1)
                ? mlsNumbers.join(', ')
                : mlsNumbers}
            </DetailTextPadded>
          )}
        </FlexColumn>

        {!featureEnabled('mlsStaffActionButtonsFeature') && (
          <ButtonsContainer>
            <StyledButton
              disabled={isAssumeDisabled}
              onClick={handleAssumeClick}
              icon={
                <StyledIcon
                  type="antdLoginOutlined"
                  height={15}
                  width={15}
                  {...(isAssumeDisabled ? { color: 'grayBase' } : {})}
                />
              }
            >
              Assume
            </StyledButton>
            <StyledButton
              type="primary"
              disabled={isChatDisabled}
              onClick={handleChatClick}
              icon={
                <Icon
                  type="chatBubble"
                  color={isChatDisabled ? 'grayBase' : 'white'}
                  height={15}
                  width={15}
                />
              }
            >
              Chat
            </StyledButton>
          </ButtonsContainer>
        )}
      </UserSectionTop>
    </>
  );
};

export default UserHeader;

const StyledIcon = styled(Icon)`
  color: rgba(0, 0, 0, 0.25);
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
  && {
    pointer-events: auto;
    ${({ disabled }) =>
      disabled &&
      `& {
      cursor: not-allowed;
      color: rgba(0, 0, 0, 0.25);
    }`}
  }
`;

const ButtonsContainer = styled.div`
  position: absolute;
  display: flex;
  gap: 14px;
  bottom: 0px;
  right: 22px;
`;

const FlexAlignPadded = styled(FlexAlign)`
  padding-top: 6px;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 31px;
`;

const UserPronouns = styled(DetailText)`
  text-transform: capitalize;
  margin-left: 8px;
`;
